import { IconFileText, IconPlaneDeparture, IconUpload, IconUserPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, residentsApi, type ResidentChangeOption, type ResidentDocument, type ResidentMember, type ResidentSection, type ResidentTrip } from "../api";
import { ErrorNote, Field, SectionTitle, Spinner } from "../components/ui";
import { rt, type ResidentKey } from "../residents-text";
import { useSession } from "../session";

const KINDS = ["citizen_card_front", "citizen_card_back", "passport", "residence_certificate", "address_proof", "enrolment_proof", "employment_proof", "household_proof", "other"] as const;
const RELATIONS = ["child", "spouse", "parent", "grandparent", "other"] as const;
const MAX_BYTES = 10 * 1024 * 1024;

const euros = (n: number) => `${n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const day = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const when = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}`;

/** A photo larger than 2000 px becomes a JPEG of that size before it is sent; PDFs go as they are. */
async function prepare(file: File): Promise<{ base64: string; mimeType: string; fileName: string; size: number }> {
  let blob: Blob = file;
  let mimeType = file.type || "application/octet-stream";
  if (mimeType.startsWith("image/") && mimeType !== "image/heic") {
    const bitmap = await createImageBitmap(file).catch(() => undefined);
    if (bitmap && Math.max(bitmap.width, bitmap.height) > 2000) {
      const scale = 2000 / Math.max(bitmap.width, bitmap.height);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      blob = await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b ?? file), "image/jpeg", 0.85));
      mimeType = "image/jpeg";
    }
  }
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return { base64: btoa(s), mimeType, fileName: file.name, size: bytes.length };
}

/**
 * The Residents section (docs/22 §12), on Azores Getaways only: the resident's account and mandate, the family and their
 * documents for the subsidy, and the flights with their tickets, invoices, claims, changes and cancellations.
 */
export function ResidentsPage() {
  const { lang } = useSession();
  const t = useCallback((key: ResidentKey, vars?: Record<string, string | number>) => rt(lang, key, vars), [lang]);
  const [data, setData] = useState<ResidentSection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await residentsApi.section());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : t("error"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (data?.notices.some((n) => !n.readAt)) void residentsApi.readNotices().catch(() => undefined);
  }, [data]);

  if (error && !data) return <main className="page"><ErrorNote onRetry={() => void load()}>{error}</ErrorNote></main>;
  if (!data) return <main className="page"><Spinner label={t("loading")} /></main>;

  const r = data.resident;
  const docsOf = (memberId: string | null) => data.household.documents.filter((d) => (d.memberId ?? null) === memberId);
  const holder = data.household.members.find((m) => m.self);
  const done = (text: string) => {
    setNotice(text);
    void load();
  };

  return (
    <main className="page">
      <div>
        <span className="eyebrow">Azores Getaways</span>
        <h1>{t("title")}</h1>
        <p className="muted">{t("lead")}</p>
      </div>
      {notice && <div className="card residents-note" role="status">{notice}</div>}
      {error && <ErrorNote>{error}</ErrorNote>}

      {data.notices.length > 0 && (
        <section className="card">
          <SectionTitle>{t("notices")}</SectionTitle>
          <div className="stack">
            {data.notices.slice(0, 5).map((n) => (
              <div key={n.id} className={`residents-notice${n.readAt ? "" : " unread"}`}>
                <div style={{ whiteSpace: "pre-wrap" }}>{n.text}</div>
                <div className="muted small">{when(n.createdAt)}{n.tripCode ? ` · ${n.tripCode}` : ""}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!r ? (
        <section className="card">
          <p>{t("notResident")}</p>
          <p className="muted">{t("notResidentHow")}</p>
        </section>
      ) : (
        <section className="card">
          <SectionTitle aside={<span className={`chip chip-${r.customer.status === "verified" ? "confirmed" : "pending"}`}>{r.customer.status === "verified" ? t("statusVerified") : r.customer.status === "pending_review" ? t("statusPending") : t("statusOther")}</span>}>{t("account")}</SectionTitle>
          <dl className="residents-kv">
            <dt>{t("mandate")}</dt>
            <dd>
              {r.mandate.status === "signed" ? t("mandateSigned", { date: r.mandate.signedAt ? day(r.mandate.signedAt) : "", v: r.mandate.version ?? 1 }) : t("mandateMissing")}
              {r.mandate.status !== "signed" && r.mandate.link && <div><a className="button button-primary button-small" href={r.mandate.link} rel="external">{t("mandateSign")}</a></div>}
            </dd>
            {r.customer.missing.filter((m) => m !== "review" && m !== "mandate").length > 0 && (
              <>
                <dt>{t("missing")}</dt>
                <dd>{r.customer.missing.filter((m) => m !== "review" && m !== "mandate").join(", ").replace(/_/g, " ")}</dd>
              </>
            )}
            {r.openAdvance > 0 && (
              <>
                <dt>{t("openAdvance")}</dt>
                <dd>{euros(r.openAdvance)}</dd>
              </>
            )}
          </dl>
        </section>
      )}

      <section className="card">
        <SectionTitle>{t("family")}</SectionTitle>
        <p className="muted small">{t("familyLead")}</p>
        <div className="stack">
          {data.household.members.map((m) => (
            <MemberCard key={m.id} member={m} documents={docsOf(m.id)} t={t} onChanged={done} />
          ))}
          {!holder && docsOf(null).length > 0 && <MemberCard member={null} documents={docsOf(null)} t={t} onChanged={done} />}
          {holder && docsOf(null).length > 0 && (
            <div className="residents-member">
              <strong>{t("householdDocs")}</strong>
              <DocumentList documents={docsOf(null)} memberId={null} t={t} onChanged={done} />
            </div>
          )}
          <AddMember t={t} onChanged={done} />
        </div>
      </section>

      {r && (
        <section className="stack">
          <SectionTitle>{t("trips")}</SectionTitle>
          {r.trips.length === 0 ? <p className="muted">{t("noTrips")}</p> : r.trips.map((trip) => <TripCard key={trip.id} trip={trip} t={t} onChanged={done} />)}
        </section>
      )}
    </main>
  );
}

type T = (key: ResidentKey, vars?: Record<string, string | number>) => string;

function MemberCard({ member, documents, t, onChanged }: { member: ResidentMember | null; documents: ResidentDocument[]; t: T; onChanged: (text: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!member) return <div className="residents-member"><DocumentList documents={documents} memberId={null} t={t} onChanged={onChanged} /></div>;
  const remove = async () => {
    if (!window.confirm(t("removeConfirm", { name: `${member.givenNames} ${member.surname}` }))) return;
    try {
      await residentsApi.removeMember(member.id);
      onChanged(`${member.givenNames} ${member.surname}: ${t("remove").toLowerCase()} ✓`);
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : t("error"));
    }
  };
  return (
    <div className="residents-member">
      <div className="residents-member-head">
        <div>
          <strong>{member.givenNames} {member.surname}</strong>
          <div className="muted small">
            {member.self ? t("holder") : t(`relation_${(member.relation ?? "other") as (typeof RELATIONS)[number]}`)}
            {member.dateOfBirth ? ` · ${day(member.dateOfBirth)}` : ""}
            {member.nif ? ` · NIF ${member.nif}` : ""}
          </div>
        </div>
        <span className={`chip chip-${member.status === "verified" ? "confirmed" : member.status === "rejected" ? "cancelled" : "pending"}`}>{member.status === "verified" ? t("memberVerified") : member.status === "rejected" ? t("memberRejected") : t("memberPending")}</span>
      </div>
      {member.note && member.status === "rejected" && <p className="small">{member.note}</p>}
      {editing ? (
        <MemberForm member={member} t={t} onDone={(text) => { setEditing(false); if (text) onChanged(text); }} />
      ) : (
        <div className="residents-actions">
          <button type="button" className="link-button" onClick={() => setEditing(true)}>{t("edit")}</button>
          {!member.self && <button type="button" className="link-button" onClick={() => void remove()}>{t("remove")}</button>}
        </div>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      <DocumentList documents={documents} memberId={member.id} t={t} onChanged={onChanged} />
    </div>
  );
}

function MemberForm({ member, t, onDone }: { member?: ResidentMember; t: T; onDone: (text?: string) => void }) {
  const [f, setF] = useState({ givenNames: member?.givenNames ?? "", surname: member?.surname ?? "", dateOfBirth: member?.dateOfBirth ?? "", nif: member?.nif ?? "", relation: member?.relation ?? "child", documentNumber: member?.documentNumber ?? "", documentExpiry: member?.documentExpiry ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = Object.fromEntries(Object.entries({ ...f, relation: member?.self ? undefined : f.relation }).filter(([, v]) => v)) as Record<string, string>;
    try {
      if (member) await residentsApi.updateMember(member.id, body);
      else await residentsApi.addMember(body);
      onDone(`${f.givenNames} ✓`);
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : t("error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="stack residents-form" onSubmit={(e) => void submit(e)}>
      <div className="residents-grid">
        <Field label={t("givenNames")}><input value={f.givenNames} onChange={set("givenNames")} required maxLength={80} /></Field>
        <Field label={t("surname")}><input value={f.surname} onChange={set("surname")} required maxLength={80} /></Field>
        <Field label={t("birthDate")}><input type="date" value={f.dateOfBirth} onChange={set("dateOfBirth")} required /></Field>
        <Field label={t("nif")}><input inputMode="numeric" pattern="\d{9}" maxLength={9} value={f.nif} onChange={set("nif")} /></Field>
        {!member?.self && (
          <Field label={t("relation")}>
            <select value={f.relation} onChange={set("relation")}>{RELATIONS.map((r) => <option key={r} value={r}>{t(`relation_${r}`)}</option>)}</select>
          </Field>
        )}
        <Field label={t("docNumber")}><input value={f.documentNumber} onChange={set("documentNumber")} maxLength={30} /></Field>
        <Field label={t("docExpiry")}><input type="date" value={f.documentExpiry} onChange={set("documentExpiry")} /></Field>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="residents-actions">
        <button type="submit" className="button button-primary button-small" disabled={busy}>{busy ? t("saving") : t("save")}</button>
        <button type="button" className="link-button" onClick={() => onDone()}>{t("cancel")}</button>
      </div>
    </form>
  );
}

function AddMember({ t, onChanged }: { t: T; onChanged: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  if (!open) return <button type="button" className="button button-small" onClick={() => setOpen(true)}><IconUserPlus size={16} /> {t("addPerson")}</button>;
  return <div className="residents-member"><MemberForm t={t} onDone={(text) => { setOpen(false); if (text) onChanged(text); }} /></div>;
}

function DocumentList({ documents, memberId, t, onChanged }: { documents: ResidentDocument[]; memberId: string | null; t: T; onChanged: (text: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<(typeof KINDS)[number]>("citizen_card_front");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = async (file: File | undefined, as = kind) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const p = await prepare(file);
      if (p.size > MAX_BYTES) throw new Error(t("tooLarge"));
      await residentsApi.uploadDocument({ ...(memberId ? { memberId } : {}), kind: as, mimeType: p.mimeType, fileName: p.fileName, base64: p.base64 });
      onChanged(`${t(`kind_${as}`)} ✓`);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t("error"));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <div className="stack residents-docs">
      <span className="field-label">{t("documents")}</span>
      {documents.length === 0 && <span className="muted small">{t("noDocuments")}</span>}
      {documents.map((d) => (
        <div key={d.id} className="residents-doc">
          <IconFileText size={18} stroke={1.5} />
          <span style={{ flex: 1 }}>
            {t(`kind_${d.kind}` as ResidentKey)}
            <span className="muted small">{d.expiresOn ? ` · ${t("expires", { date: day(d.expiresOn) })}` : ""}</span>
          </span>
          <span className={`chip chip-${d.status === "verified" ? "confirmed" : d.status === "received" ? "pending" : "cancelled"}`}>{t(`doc_${d.status === "replaced" ? "expired" : d.status}` as ResidentKey)}</span>
          {d.hasFile && <a className="link-button" href={`/api/v1/residents/documents/${d.id}/file`} target="_blank" rel="noopener noreferrer">{t("view")}</a>}
          <label className="link-button" style={{ cursor: "pointer" }}>
            {t("replace")}
            <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => void upload(e.target.files?.[0], d.kind as (typeof KINDS)[number])} />
          </label>
        </div>
      ))}
      <div className="residents-upload">
        <select value={kind} onChange={(e) => setKind(e.target.value as (typeof KINDS)[number])} aria-label={t("kind")}>
          {KINDS.map((k) => <option key={k} value={k}>{t(`kind_${k}`)}</option>)}
        </select>
        <label className="button button-small" style={{ cursor: busy ? "wait" : "pointer" }}>
          <IconUpload size={16} /> {busy ? t("uploading") : t("upload")}
          <input ref={input} type="file" accept="image/*,application/pdf" capture="environment" style={{ display: "none" }} disabled={busy} onChange={(e) => void upload(e.target.files?.[0])} />
        </label>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

function TripCard({ trip, t, onChanged }: { trip: ResidentTrip; t: T; onChanged: (text: string) => void }) {
  const [mode, setMode] = useState<"change" | null>(null);
  const [journey, setJourney] = useState(0);
  const [date, setDate] = useState("");
  const [options, setOptions] = useState<ResidentChangeOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fail = (err: unknown) => setError(err instanceof ApiError && err.message ? err.message : t("error"));
  const search = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setOptions((await residentsApi.changeOptions(trip.code, journey, date)).options);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const choose = async (o: ResidentChangeOption) => {
    const flights = o.legs[journey]?.flights.join("+") ?? "";
    if (!window.confirm(t("confirmChange", { flights, date: day(date), amount: euros(Math.max(0, o.customerPays)) }))) return;
    setBusy(true);
    try {
      const r = await residentsApi.change(trip.code, { journey, date, optionKey: o.optionKey });
      if (r.paymentUrl) window.location.assign(r.paymentUrl);
      setMode(null);
      setOptions(null);
      onChanged(t("changed"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const q = await residentsApi.cancellationQuote(trip.code);
      if (!window.confirm(t("cancelConfirm", { explanation: q.explanation }))) return;
      await residentsApi.cancel(trip.code);
      onChanged(t("cancelled"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const statusKey = `trip_${trip.status}` as ResidentKey;
  return (
    <section className="card">
      <div className="residents-member-head">
        <div>
          <span className="eyebrow">{trip.code} · {trip.orderReference}{trip.pnr ? ` · ${trip.pnr}` : ""}</span>
          {trip.legs.map((l, i) => (
            <div key={i}><IconPlaneDeparture size={16} stroke={1.5} /> <strong>{l.flights.join("+")}</strong> {l.origin}→{l.destination} {when(l.departure)}</div>
          ))}
        </div>
        <span className={`chip chip-${trip.status === "ticketed" ? "confirmed" : trip.status === "cancelled" || trip.status === "failed" ? "cancelled" : "pending"}`}>{t(statusKey)}</span>
      </div>
      <dl className="residents-kv">
        <dt>{t("youPaid")}</dt>
        <dd><strong>{euros(trip.customerPays)}</strong></dd>
        <dt>{t("subsidy")}</dt>
        <dd>{euros(trip.subsidyTotal)}{trip.advanceTotal > 0 ? ` (${euros(trip.advanceTotal)} ${t("advanced")})` : ""}</dd>
      </dl>
      {trip.passengers.length > 0 && (
        <div className="muted small">{trip.passengers.map((p) => `${p.name}${p.tickets.length ? ` · ${p.tickets.join(", ")}` : ""}`).join(" — ")}</div>
      )}
      {trip.documents.length > 0 && (
        <div className="residents-actions">
          {trip.documents.map((d) => <a key={d.kind} className="button button-small" href={d.url} target="_blank" rel="noopener noreferrer"><IconFileText size={16} /> {d.label}</a>)}
        </div>
      )}
      {trip.claims.length > 0 && (
        <div className="stack">
          <span className="field-label">{t("claims")}</span>
          {trip.claims.map((c, i) => (
            <div key={i} className="residents-doc">
              <span style={{ flex: 1 }}>{c.passenger}{c.reference ? <span className="muted small"> · {c.reference}</span> : null}</span>
              <span>{euros(c.amount)}</span>
              <span className={`chip chip-${c.status === "paid" ? "confirmed" : c.status === "refused" ? "cancelled" : "pending"}`}>{t(`claim_${c.status}` as ResidentKey)}</span>
            </div>
          ))}
        </div>
      )}
      {trip.collections.filter((x) => x.status === "open").map((x) => (
        <div key={x.url} className="residents-doc">
          <span style={{ flex: 1 }}>{t("toPay")}: {x.description}</span>
          <strong>{euros(x.amount)}</strong>
          <a className="button button-primary button-small" href={x.url} rel="external">{t("pay")}</a>
        </div>
      ))}
      {error && <ErrorNote>{error}</ErrorNote>}
      {mode === "change" ? (
        <form className="stack residents-form" onSubmit={(e) => void search(e)}>
          <div className="residents-grid">
            {trip.legs.length > 1 && (
              <Field label={t("journey")}>
                <select value={journey} onChange={(e) => { setJourney(Number(e.target.value)); setOptions(null); }}>
                  <option value={0}>{t("outbound")}</option>
                  <option value={1}>{t("return")}</option>
                </select>
              </Field>
            )}
            <Field label={t("newDate")}><input type="date" value={date} onChange={(e) => { setDate(e.target.value); setOptions(null); }} required /></Field>
          </div>
          <div className="residents-actions">
            <button type="submit" className="button button-small" disabled={busy || !date}>{t("searchOptions")}</button>
            <button type="button" className="link-button" onClick={() => { setMode(null); setOptions(null); }}>{t("cancel")}</button>
          </div>
          {options && options.length === 0 && <p className="muted">{t("noOptions")}</p>}
          {options?.map((o) => (
            <div key={o.optionKey} className="residents-doc">
              <span style={{ flex: 1 }}>
                {o.legs[journey]?.flights.join("+")} {o.legs[journey] ? when(o.legs[journey]!.departure) : ""}
                <span className="muted small"> · {t("penalty", { amount: euros(o.penalty) })}</span>
              </span>
              <strong>{t("youPay", { amount: euros(Math.max(0, o.customerPays)) })}</strong>
              <button type="button" className="button button-primary button-small" disabled={busy} onClick={() => void choose(o)}>{t("choose")}</button>
            </div>
          ))}
        </form>
      ) : (
        <div className="residents-actions">
          {trip.canChange && <button type="button" className="button button-small" disabled={busy} onClick={() => setMode("change")}>{t("change")}</button>}
          {trip.canCancel && <button type="button" className="link-button" disabled={busy} onClick={() => void cancel()}>{t("cancelTrip")}</button>}
        </div>
      )}
    </section>
  );
}
